import { useDataStore } from '../stores/dataStore.js';
import { eventsAPI } from '../services/api/EventsAPI.js';

export class MineDetailsPanel {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      enableFeedback: true,
      showCoordinates: true,
      showRiskData: false,  // Risk data shown in Risk Details panel
      showEvents: false,    // News and earthquakes shown in Risk Details panel
      ...options
    };
    this.currentMine = null;
    this.unsubscribe = null;
    this.eventsData = null;
    this.init();
  }

  init() {
    this.render();
    this.attachEventListeners();

    // Only subscribe to store if in browser environment
    if (typeof window !== 'undefined') {
      this.subscribeToStore();
    }
  }

  render() {
    this.container.innerHTML = `
      <div class="sidebar-header">
        <h3 class="sidebar-title">Mine Details</h3>
        <i class="fas fa-times sidebar-close" id="mineDetailsSidebarClose"></i>
      </div>
      <div class="sidebar-content">
        <div class="sidebar-section">
          <h4 class="sidebar-section-title">
            <i class="fas fa-info-circle sidebar-section-icon"></i>
            Information
          </h4>
          <div class="mine-info-grid">
            <div class="mine-info-row">
              <div class="mine-info-label">Name</div>
              <div class="mine-info-value" id="mineDetailsName">-</div>
            </div>
            <div class="mine-info-row">
              <div class="mine-info-label">Commodity</div>
              <div class="mine-info-value" id="mineDetailsCommodity">-</div>
            </div>
            <div class="mine-info-row">
              <div class="mine-info-label">Type</div>
              <div class="mine-info-value" id="mineDetailsType">-</div>
            </div>
            <div class="mine-info-row">
              <div class="mine-info-label">Status</div>
              <div class="mine-info-value" id="mineDetailsStatus">-</div>
            </div>
            <div class="mine-info-row">
              <div class="mine-info-label">Country</div>
              <div class="mine-info-value" id="mineDetailsCountry">-</div>
            </div>
            ${this.options.showCoordinates ? `
            <div class="mine-info-row">
              <div class="mine-info-label">Coordinates</div>
              <div class="mine-info-value" id="mineDetailsCoordinates">-</div>
            </div>
            ` : ''}
          </div>
        </div>

        ${this.options.showRiskData ? `
        <div class="sidebar-section">
          <h4 class="sidebar-section-title">
            <i class="fas fa-exclamation-triangle sidebar-section-icon"></i>
            Risk Assessment
          </h4>
          <div class="risk-source-note" id="mineDetailsRiskSource"></div>
          <div class="risk-indicator">
            <div class="risk-bar">
              <div class="risk-level" id="mineDetailsRiskLevel"></div>
            </div>
            <div class="risk-value" id="mineDetailsRiskValue">-</div>
          </div>
        </div>
        ` : ''}

        ${this.options.showEvents ? `
        <div class="sidebar-section" id="newsEventsSection">
          <h4 class="sidebar-section-title">
            <i class="fas fa-newspaper sidebar-section-icon"></i>
            Recent News
            <span class="events-count" id="newsCount"></span>
          </h4>
          <div class="events-container" id="newsEventsContainer">
            <div class="events-loading">
              <i class="fas fa-spinner fa-spin"></i> Loading news...
            </div>
          </div>
        </div>

        <div class="sidebar-section" id="earthquakesSection">
          <h4 class="sidebar-section-title">
            <i class="fas fa-globe sidebar-section-icon"></i>
            Seismic Activity
            <span class="events-count" id="earthquakesCount"></span>
          </h4>
          <div class="events-container" id="earthquakesContainer">
            <div class="events-loading">
              <i class="fas fa-spinner fa-spin"></i> Loading seismic data...
            </div>
          </div>
        </div>
        ` : ''}

        ${this.options.enableFeedback ? `
        <div class="sidebar-section">
          <button class="filter-button apply" style="width: 100%;" id="openFeedbackModal">
            <i class="fas fa-envelope" style="margin-right: 8px;"></i>
            Send Feedback
          </button>
        </div>
        ` : ''}
      </div>
    `;
  }

  attachEventListeners() {
    // Close button
    const closeBtn = this.container.querySelector('#mineDetailsSidebarClose');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hide();
      });
    }

    // Feedback button
    if (this.options.enableFeedback) {
      const feedbackBtn = this.container.querySelector('#openFeedbackModal');
      if (feedbackBtn) {
        feedbackBtn.addEventListener('click', () => {
          this.openFeedbackModal();
        });
      }
    }

    // Close when clicking overlay
    const overlay = document.getElementById('mineDetailsOverlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.hide();
      });
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.hide();
      }
    });
  }

  subscribeToStore() {
    this.unsubscribe = useDataStore.subscribe(
      (state) => state.selectedMine,
      (mine) => {
        if (mine) {
          this.show(mine);
        } else {
          this.hide();
        }
      }
    );

    // Initialize with current selection
    const currentSelection = useDataStore.getState().selectedMine;
    if (currentSelection) {
      this.show(currentSelection);
    }
  }

  show(mine) {
    this.currentMine = mine;
    this.updateContent(mine);
    this.container.classList.add('visible');

    const overlay = document.getElementById('mineDetailsOverlay');
    if (overlay) {
      overlay.classList.add('visible');
    }

    // Fetch events data (news and earthquakes) if enabled
    if (this.options.showEvents && mine.mine_id) {
      this.fetchAndDisplayEvents(mine.mine_id);
    }

    // Dispatch event
    const event = new CustomEvent('mineDetailsShown', {
      detail: { mine }
    });
    this.container.dispatchEvent(event);
  }

  async showMine(mineId) {
    if (!mineId) {
      console.error('MineDetailsPanel: No mine ID provided');
      return;
    }

    try {
      const dataStore = useDataStore.getState();

      // Always fetch full mine details from API to ensure we have complete data
      // including risk scores which are not included in viewport data
      console.log('MineDetailsPanel - Fetching full mine details for ID:', mineId);
      const mineDetails = await dataStore.fetchMineDetails(mineId);

      if (mineDetails) {
        this.show(mineDetails);
      } else {
        console.error('MineDetailsPanel: No mine data returned from service');
      }
    } catch (error) {
      console.error('MineDetailsPanel: Error fetching mine details:', error);
    }
  }

  hide() {
    this.currentMine = null;
    this.container.classList.remove('visible');

    const overlay = document.getElementById('mineDetailsOverlay');
    if (overlay) {
      overlay.classList.remove('visible');
    }

    // Clear selection in store
    const dataStore = useDataStore.getState();
    if (dataStore.selectedMine) {
      dataStore.setSelectedMine(null);
    }

    // Dispatch event
    const event = new CustomEvent('mineDetailsHidden');
    this.container.dispatchEvent(event);
  }

  updateContent(mine) {
    // Debug: Log the mine data structure to see what we're working with
    console.log('MineDetailsPanel - Full mine data:', mine);
    console.log('MineDetailsPanel - mine.commodity:', mine.commodity);
    console.log('MineDetailsPanel - Type of mine.commodity:', typeof mine.commodity);

    // Basic information
    this.setElementText('#mineDetailsName', mine.mine_name || 'Unknown');

    // Handle commodity display - prefer mine_commodity junction table for multi-commodity mines
    let commodityName = 'Unknown';
    if (mine.mine_commodity && Array.isArray(mine.mine_commodity) && mine.mine_commodity.length > 0) {
      commodityName = mine.mine_commodity
        .map(mc => mc.commodity?.commodity_name)
        .filter(Boolean)
        .join(', ');
    } else if (mine.commodity && typeof mine.commodity === 'object' && mine.commodity.commodity_name) {
      commodityName = mine.commodity.commodity_name;
    } else if (typeof mine.commodity === 'string') {
      commodityName = mine.commodity;
    } else if (mine.primary_commodity) {
      commodityName = mine.primary_commodity;
    } else if (mine.commodity_name) {
      commodityName = mine.commodity_name;
    }

    this.setElementText('#mineDetailsCommodity', commodityName);

    this.setElementText('#mineDetailsType', mine.mine_type || 'Unknown');
    this.setElementText('#mineDetailsStatus', mine.mine_status || mine.status || 'Unknown');

    // Handle country display - check for nested country object first
    const countryName = mine.country?.country_name || mine.country || 'Unknown';
    this.setElementText('#mineDetailsCountry', countryName);

    // Coordinates
    if (this.options.showCoordinates) {
      const coords = mine.latitude && mine.longitude
        ? `${mine.latitude.toFixed(4)}, ${mine.longitude.toFixed(4)}`
        : 'Unknown';
      this.setElementText('#mineDetailsCoordinates', coords);
    }

    // Risk data
    if (this.options.showRiskData) {
      this.updateRiskIndicator(mine);
    }
  }

  updateRiskIndicator(mine) {
    const riskLevel = this.container.querySelector('#mineDetailsRiskLevel');
    const riskValue = this.container.querySelector('#mineDetailsRiskValue');
    const riskSource = this.container.querySelector('#mineDetailsRiskSource');

    // Check for new risk scoring data structure (mine_risk_aggregate)
    // The data comes flattened from mapHelpers.js
    // IMPORTANT: Prioritize new MVP scoring (risk_overall_score) over legacy scoring (overall_risk_score)
    const riskScore = mine.risk_overall_score ?? mine.risk_score ?? mine.overall_risk_score;
    const riskClassification = mine.risk_classification;

    // Determine if this is a site-level scored mine or a country-level default
    const isSiteScored = mine.risk_overall_score !== undefined && mine.risk_overall_score !== null;

    if (riskSource) {
      if (riskScore !== undefined && riskScore !== null) {
        riskSource.textContent = isSiteScored
          ? 'Site-level risk assessment'
          : 'Default country-level risk score';
      } else {
        riskSource.textContent = '';
      }
    }

    if (riskScore !== undefined && riskScore !== null) {
      const rawScore = parseFloat(riskScore);
      // Convert 0-10 scale to 0-100 percentage for display
      const percentageScore = rawScore <= 10 ? rawScore * 10 : rawScore;

      // Use classification if available, otherwise calculate category
      const category = riskClassification ?
        riskClassification.toLowerCase().replace(/\s+/g, '-') :
        this.getRiskCategory(percentageScore);

      // Update risk bar
      if (riskLevel) {
        riskLevel.style.width = `${Math.min(100, Math.max(0, percentageScore))}%`;
        riskLevel.className = `risk-level risk-${category}`;
      }

      // Update risk value - text should not have colored background
      if (riskValue) {
        const label = riskClassification || this.getRiskLabel(category);
        riskValue.textContent = `${rawScore.toFixed(1)}/10 (${label})`;
        riskValue.className = 'risk-value'; // Remove category-specific class to avoid colored background
      }
    } else {
      // No risk data available
      if (riskLevel) {
        riskLevel.style.width = '0%';
        riskLevel.className = 'risk-level';
      }
      if (riskValue) {
        riskValue.textContent = 'No risk data available';
        riskValue.className = 'risk-value';
      }
    }
  }

  getRiskCategory(score) {
    if (score >= 90) return 'very-high';
    if (score >= 70) return 'high';
    if (score >= 50) return 'medium';
    if (score >= 30) return 'low';
    return 'very-low';
  }

  getRiskLabel(category) {
    const labels = {
      'very-high': 'Very High Risk',
      'high': 'High Risk',
      'medium': 'Medium Risk',
      'low': 'Low Risk',
      'very-low': 'Very Low Risk'
    };
    return labels[category] || 'Unknown Risk';
  }

  setElementText(selector, text) {
    const element = this.container.querySelector(selector);
    if (element) {
      element.textContent = text;
    }
  }

  openFeedbackModal() {
    if (!this.currentMine) return;

    // Dispatch event to open feedback modal
    const event = new CustomEvent('openFeedbackModal', {
      detail: {
        mine: this.currentMine,
        context: 'mine-details'
      }
    });
    document.dispatchEvent(event);
  }

  isVisible() {
    return this.container.classList.contains('visible');
  }

  getCurrentMine() {
    return this.currentMine;
  }

  /**
   * Fetch and display news and earthquake events for a mine
   * Uses smart caching - only fetches from API if cache is >24h old
   */
  async fetchAndDisplayEvents(mineId) {
    try {
      // Reset loading states
      this.showEventsLoading('news');
      this.showEventsLoading('earthquakes');

      // Fetch both in parallel
      const result = await eventsAPI.getMineEvents(mineId, { days: 30, maxAge: 24 });

      if (result.success && result.data) {
        // Render news events
        if (result.data.news) {
          this.renderNewsEvents(result.data.news);
        } else if (result.data.errors?.news) {
          this.showEventsError('news', result.data.errors.news);
        }

        // Render earthquake data
        if (result.data.earthquakes) {
          this.renderEarthquakes(result.data.earthquakes);
        } else if (result.data.errors?.earthquakes) {
          this.showEventsError('earthquakes', result.data.errors.earthquakes);
        }
      }
    } catch (error) {
      console.error('MineDetailsPanel: Error fetching events:', error);
      this.showEventsError('news', 'Failed to load news');
      this.showEventsError('earthquakes', 'Failed to load seismic data');
    }
  }

  showEventsLoading(type) {
    const containerId = type === 'news' ? 'newsEventsContainer' : 'earthquakesContainer';
    const container = this.container.querySelector(`#${containerId}`);
    if (container) {
      container.innerHTML = `
        <div class="events-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading...
        </div>
      `;
    }
  }

  showEventsError(type, message) {
    const containerId = type === 'news' ? 'newsEventsContainer' : 'earthquakesContainer';
    const container = this.container.querySelector(`#${containerId}`);
    if (container) {
      container.innerHTML = `
        <div class="events-empty">
          <i class="fas fa-exclamation-circle"></i> ${message}
        </div>
      `;
    }
  }

  renderNewsEvents(newsData) {
    const container = this.container.querySelector('#newsEventsContainer');
    const countBadge = this.container.querySelector('#newsCount');

    if (!container) return;

    const events = newsData.events || [];
    const count = events.length;

    // Update count badge
    if (countBadge) {
      countBadge.textContent = count > 0 ? `(${count})` : '';
    }

    if (count === 0) {
      container.innerHTML = `
        <div class="events-empty">
          <i class="fas fa-check-circle"></i> No recent news events
        </div>
      `;
      return;
    }

    // Show cache status if data was refreshed
    const cacheInfo = newsData.refreshed
      ? `<div class="cache-status refreshed">Data refreshed just now</div>`
      : newsData.cache_age_hours
        ? `<div class="cache-status">Updated ${Math.round(newsData.cache_age_hours)}h ago</div>`
        : '';

    // Render top 5 news items
    const newsItems = events.slice(0, 5).map(event => this.renderNewsItem(event)).join('');

    container.innerHTML = `
      ${cacheInfo}
      <div class="events-list">
        ${newsItems}
      </div>
      ${count > 5 ? `<div class="events-more">+${count - 5} more articles</div>` : ''}
    `;
  }

  renderNewsItem(event) {
    const date = event.article_published_at
      ? new Date(event.article_published_at).toLocaleDateString()
      : 'Unknown date';

    const severityClass = this.getSeverityClass(event.severity_score);
    const eventTypeIcon = this.getEventTypeIcon(event.event_type);

    return `
      <div class="event-item news-item ${severityClass}">
        <div class="event-header">
          <span class="event-type-icon" title="${event.event_type || 'news'}">
            <i class="fas ${eventTypeIcon}"></i>
          </span>
          <span class="event-date">${date}</span>
          ${event.relevance_score ? `<span class="relevance-badge">Rel: ${parseFloat(event.relevance_score).toFixed(1)}</span>` : ''}
          ${event.severity_score ? `<span class="severity-badge ${severityClass}">${event.severity_score.toFixed(1)}</span>` : ''}
        </div>
        <div class="event-title">
          ${event.article_url
            ? `<a href="${event.article_url}" target="_blank" rel="noopener">${event.article_title || 'Untitled'}</a>`
            : event.article_title || 'Untitled'
          }
        </div>
        <div class="event-source">${event.article_domain || 'Unknown source'}</div>
      </div>
    `;
  }

  renderEarthquakes(earthquakeData) {
    const container = this.container.querySelector('#earthquakesContainer');
    const countBadge = this.container.querySelector('#earthquakesCount');

    if (!container) return;

    const earthquakes = earthquakeData.earthquakes || [];
    const count = earthquakes.length;

    // Update count badge
    if (countBadge) {
      countBadge.textContent = count > 0 ? `(${count})` : '';
    }

    if (count === 0) {
      container.innerHTML = `
        <div class="events-empty">
          <i class="fas fa-check-circle"></i> No significant seismic activity
        </div>
      `;
      return;
    }

    // Show cache status
    const cacheInfo = earthquakeData.refreshed
      ? `<div class="cache-status refreshed">Data refreshed just now</div>`
      : earthquakeData.cache_age_hours
        ? `<div class="cache-status">Updated ${Math.round(earthquakeData.cache_age_hours)}h ago</div>`
        : '';

    // Render earthquake items
    const items = earthquakes.slice(0, 5).map(eq => this.renderEarthquakeItem(eq)).join('');

    container.innerHTML = `
      ${cacheInfo}
      <div class="events-list">
        ${items}
      </div>
      ${count > 5 ? `<div class="events-more">+${count - 5} more earthquakes</div>` : ''}
    `;
  }

  renderEarthquakeItem(earthquake) {
    const date = earthquake.event_time
      ? new Date(earthquake.event_time).toLocaleDateString()
      : 'Unknown date';

    const magnitude = earthquake.magnitude?.toFixed(1) || '?';
    const magnitudeClass = this.getMagnitudeClass(earthquake.magnitude);
    const distance = earthquake.distance_km
      ? `${Math.round(earthquake.distance_km)} km away`
      : '';

    return `
      <div class="event-item earthquake-item ${magnitudeClass}">
        <div class="event-header">
          <span class="magnitude-badge ${magnitudeClass}">M${magnitude}</span>
          <span class="event-date">${date}</span>
          ${earthquake.proximity_level ? `<span class="proximity-badge">${earthquake.proximity_level}</span>` : ''}
        </div>
        <div class="event-title">${earthquake.place_description || 'Unknown location'}</div>
        <div class="event-details">
          ${distance}
          ${earthquake.depth_km ? ` • Depth: ${Math.round(earthquake.depth_km)} km` : ''}
        </div>
      </div>
    `;
  }

  getSeverityClass(score) {
    if (!score) return '';
    if (score >= 7) return 'severity-high';
    if (score >= 4) return 'severity-medium';
    return 'severity-low';
  }

  getMagnitudeClass(magnitude) {
    if (!magnitude) return '';
    if (magnitude >= 6) return 'magnitude-high';
    if (magnitude >= 5) return 'magnitude-medium';
    return 'magnitude-low';
  }

  getEventTypeIcon(eventType) {
    const icons = {
      conflict: 'fa-fire',
      governance: 'fa-landmark',
      environmental: 'fa-leaf',
      operational: 'fa-cogs',
      economic: 'fa-chart-line',
      other: 'fa-newspaper'
    };
    return icons[eventType] || icons.other;
  }

  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.hide();
    this.container.innerHTML = '';
  }
}